import { ReactNode, useState } from 'react';

import Button from '../../../components/Button/Button';
import LinkButton from '../../../components/Button/LinkButton';
import InlineIcon from '../../../components/InlineIcon/InlineIcon';
import MaxWidth from '../../../components/MaxWidth/MaxWidth';
import Paragraph from '../../../components/Paragraph/Paragraph';
import Relative from '../../../components/Relative/Relative';
import Row from '../../../components/Row/Row';
import Spacer from '../../../components/Spacer/Spacer';
import Stack from '../../../components/Stack/Stack';
import Text from '../../../components/Text/Text';
import TopRightPopOver from '../../../components/TopRightPopOver/TopRightPopOver';
import useTrans from '../../../hooks/ui/useTrans';
import useDraftBookingStepProps from '../../pages/BookingForm/hooks/useDraftBookingStepProps';
import LoggedInCustomer from '../../types/LoggedInCustomer';
import NavigationItem from '../../types/NavigationItem';
import LogOutButton from '../LogOutButton/LogOutButton';
import OverlayHeaderButton from '../OverlayHeaderButton/OverlayHeaderButton';

import dotIcon from './graphics/dot.svg?raw';
import loggedInIcon from './graphics/loggedIn.svg?raw';

type OptionButtonProps = {
    children: ReactNode;
    href: string;
};

const OptionButton = ({ children, href }: OptionButtonProps) => (
    <LinkButton variant="text" fullWidth size="xsmall" href={href}>
        <Row space="small">
            <InlineIcon src={dotIcon} color="light" />
            <Text lineHeight="small">{children}</Text>
        </Row>
    </LinkButton>
);

type OverlayNavigationProps = {
    navigationUrls: Array<NavigationItem>;
    loggedInCustomer?: LoggedInCustomer;
    preventLogout?: boolean;
};

const OverlayNavigation = ({
    navigationUrls,
    loggedInCustomer,
    preventLogout,
}: OverlayNavigationProps) => {
    const trans = useTrans('CustomerArea.Shared.LoggedInCustomerButton.');
    const transShared = useTrans('CustomerArea.Shared.Navigation.');
    const transNavigation = useTrans();
    const [isOpen, setIsOpen] = useState(false);

    /**
     * The booking is only available in later steps of the booking form.
     */
    const { booking } = useDraftBookingStepProps();

    const handleClickOutside = () => {
        setTimeout(() => setIsOpen(false), 10);
    };

    return (
        <Relative>
            <OverlayHeaderButton
                icon={loggedInIcon}
                onClick={() => setIsOpen(!isOpen)}
            >
                <MaxWidth width={100}>
                    <Paragraph lineHeight="small" hideOverflow>
                        {trans('Label')}
                    </Paragraph>
                </MaxWidth>
            </OverlayHeaderButton>
            <TopRightPopOver
                isOpen={isOpen}
                onClickOutside={handleClickOutside}
                width={150}
                noPadding
            >
                <Stack space="none">
                    <Spacer vertical="small" />
                    <Button
                        onClick={() => setIsOpen(false)}
                        variant="text"
                        size="medium-narrow"
                        fullWidth
                    >
                        <Row space="small" vertical="middle">
                            <InlineIcon src={loggedInIcon} />
                            <MaxWidth width={100}>
                                <Paragraph
                                    lineHeight="small"
                                    hideOverflow
                                    align="left"
                                >
                                    {trans('Label')}
                                </Paragraph>
                            </MaxWidth>
                        </Row>
                    </Button>

                    {navigationUrls.map((item) => {
                        return (
                            <OptionButton href={item.url} key={item.labelKey}>
                                {transNavigation(item.labelKey)}
                            </OptionButton>
                        );
                    })}

                    {loggedInCustomer && !preventLogout && (
                        <LogOutButton
                            actionUrl={loggedInCustomer.logOutUrl}
                            bookingReference={booking?.reference}
                            variant="text"
                            fullWidth
                            size="xsmall"
                        >
                            <Row space="small">
                                <InlineIcon src={dotIcon} color="light" />
                                <Text lineHeight="small">
                                    {transShared('LogOut')}
                                </Text>
                            </Row>
                        </LogOutButton>
                    )}

                    <Spacer vertical="small" />
                </Stack>
            </TopRightPopOver>
        </Relative>
    );
};

export default OverlayNavigation;
